// src/hooks/useRealTimeLocation.ts - Fixed version with better connection management
import { useState, useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

interface LocationData {
  latitude: number;
  longitude: number;
  timestamp: string;
  speed?: number;
  heading?: number;
  ignition?: boolean;
  address?: string;
}

interface DeviceLocationUpdate {
  imei: string;
  location: LocationData | null;
  message?: string;
}

interface ConnectionStats {
  connectedUsers: number;
  totalSubscriptions: number;
  rooms: number;
}

interface UseRealTimeLocationReturn {
  currentLocation: LocationData | null;
  locationHistory: LocationData[];
  isConnected: boolean;
  isSubscribed: boolean;
  error: string | null;
  isMoving: boolean;
  connectionStats: ConnectionStats | null;
  subscribeToDevice: (imei: string) => void;
  unsubscribeFromDevice: () => void;
  reconnect: () => void;
  clearError: () => void;
}

export const useRealTimeLocation = (
  selectedDeviceImei?: string
): UseRealTimeLocationReturn => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [currentLocation, setCurrentLocation] = useState<LocationData | null>(null);
  const [locationHistory, setLocationHistory] = useState<LocationData[]>([]);
  const [previousLocation, setPreviousLocation] = useState<LocationData | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subscribedImei, setSubscribedImei] = useState<string | null>(null);
  const [isMoving, setIsMoving] = useState(false);
  const [connectionStats, setConnectionStats] = useState<ConnectionStats | null>(null);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  // Refs for stable references
  const subscribedImeiRef = useRef<string | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const movementTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const socketRef = useRef<Socket | null>(null);

  subscribedImeiRef.current = subscribedImei;
  socketRef.current = socket;

  // Enhanced movement calculation
  const calculateMovement = useCallback((newLocation: LocationData, oldLocation: LocationData | null) => {
    if (!oldLocation) return false;
    
    // Primary check: speed from device (increased threshold for accuracy)
    if (newLocation.speed !== undefined && newLocation.speed > 5) {
      return true;
    }
    
    // Secondary check: ignition status change
    if (newLocation.ignition !== undefined && oldLocation.ignition !== undefined) {
      if (newLocation.ignition !== oldLocation.ignition) {
        return newLocation.ignition; // Only moving if ignition is ON
      }
    }
    
    // Tertiary check: significant position change
    const timeDiff = new Date(newLocation.timestamp).getTime() - new Date(oldLocation.timestamp).getTime();
    if (timeDiff > 5000) { // Only check if more than 5 seconds apart
      const lat1 = oldLocation.latitude;
      const lon1 = oldLocation.longitude;
      const lat2 = newLocation.latitude;
      const lon2 = newLocation.longitude;
      
      // Haversine formula for distance calculation
      const R = 6371e3; // Earth radius in meters
      const φ1 = lat1 * Math.PI/180;
      const φ2 = lat2 * Math.PI/180;
      const Δφ = (lat2-lat1) * Math.PI/180;
      const Δλ = (lon2-lon1) * Math.PI/180;

      const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
                Math.cos(φ1) * Math.cos(φ2) *
                Math.sin(Δλ/2) * Math.sin(Δλ/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

      const distance = R * c; // Distance in meters
      
      // Consider moving if moved more than 10 meters
      return distance > 10;
    }
    
    return false;
  }, []);

  // Enhanced subscribe function
  const subscribeToDevice = useCallback((imei: string) => {
    const currentSocket = socketRef.current;
    if (!currentSocket || !isConnected) {
      setError('Not connected to real-time service. Please check connection.');
      return;
    }

    // Unsubscribe from previous device if any
    if (subscribedImeiRef.current && subscribedImeiRef.current !== imei) {
      console.log('Unsubscribing from previous device:', subscribedImeiRef.current);
      currentSocket.emit('unsubscribe_from_device', { imei: subscribedImeiRef.current });
      setIsSubscribed(false);
    }

    console.log('Subscribing to device:', imei);
    
    // Reset state for new device
    setPreviousLocation(null);
    setCurrentLocation(null);
    setLocationHistory([]);
    setIsMoving(false);
    setError(null);
    
    // Subscribe to device
    currentSocket.emit('subscribe_to_device', { imei });
    currentSocket.emit('get_real_time_telemetry', { imei });
    setSubscribedImei(imei);
    
    // Set a timeout to check if subscription was successful
    setTimeout(() => {
      if (!isSubscribed) {
        setError(`Failed to subscribe to device ${imei}. Device may be offline.`);
      }
    }, 5000);
  }, [isConnected, isSubscribed]);

  const unsubscribeFromDevice = useCallback(() => {
    const currentSocket = socketRef.current;
    const currentImei = subscribedImeiRef.current;
    
    if (!currentSocket || !currentImei) return;

    console.log('Unsubscribing from device:', currentImei);
    currentSocket.emit('unsubscribe_from_device', { imei: currentImei });
    
    setSubscribedImei(null);
    setIsSubscribed(false);
    setPreviousLocation(null);
    setCurrentLocation(null);
    setLocationHistory([]);
    setIsMoving(false);
    setError(null);
  }, []);

  const reconnect = useCallback(() => {
    const currentSocket = socketRef.current;
    if (currentSocket) {
      console.log('Manual reconnection requested...');
      setError('Reconnecting...');
      currentSocket.disconnect();
      setTimeout(() => {
        currentSocket.connect();
      }, 1000);
      setReconnectAttempt(prev => prev + 1);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Initialize socket connection with better URL handling
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found. Please log in again.');
      return;
    }

    let socketUrl: string;
    
    if (process.env.NODE_ENV === 'production') {
      // Production: Use same domain as API
      if (process.env.REACT_APP_API_URL) {
        // Remove /api and use base URL
        // https://app.scetru.com/api → https://app.scetru.com
        socketUrl = process.env.REACT_APP_API_URL.replace(/\/api$/, '');
      } else {
        // Fallback to current origin
        socketUrl = window.location.origin;
      }
    } else {
      // Development: Use localhost
      socketUrl = 'http://localhost:1001';
    }

    console.log('🔌 Connecting to Socket.IO at:', socketUrl);
    console.log('   Environment:', process.env.NODE_ENV);
    console.log('   API URL:', process.env.REACT_APP_API_URL);


    console.log('Connecting to Socket.IO at:', socketUrl);

    const socketInstance = io(socketUrl, {
      auth: { token },
      transports: ['websocket', 'polling'], // Added polling as fallback
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      forceNew: true, // Force new connection    
      upgrade: true,
      rememberUpgrade: true,
    });

    // Enhanced connection events
    socketInstance.on('connect', () => {
      console.log('✅ Connected to real-time location service');
      setIsConnected(true);
      setError(null);
      setReconnectAttempt(0);
      
      // Clear any pending reconnect timeouts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Request connection stats
      socketInstance.emit('get_connection_stats');
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('❌ Disconnected from real-time location service:', reason);
      setIsConnected(false);
      setIsSubscribed(false);
      
      if (reason === 'io server disconnect') {
        setError('Server disconnected. Attempting to reconnect...');
      } else if (reason === 'transport close') {
        setError('Connection lost. Reconnecting...');
      }
    });

    socketInstance.on('connect_error', (error) => {
      console.error('🔴 Socket connection error:', error.message);
      setIsConnected(false);
      setError(`Connection failed: ${error.message}`);
      
      // Implement exponential backoff for reconnection
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempt), 30000);
      console.log(`Retrying connection in ${delay}ms (attempt ${reconnectAttempt + 1})`);
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      reconnectTimeoutRef.current = setTimeout(() => {
        setReconnectAttempt(prev => prev + 1);
      }, delay);
    });

    // Connection confirmation
    socketInstance.on('connection_established', (data) => {
      console.log('✅ Connection confirmed:', data);
      setConnectionStats(data.stats || {
        connectedUsers: 1,
        totalSubscriptions: 0,
        rooms: 0
      });
    });

    // Enhanced device location updates with validation
    socketInstance.on('device_location_update', (data: DeviceLocationUpdate) => {
      console.log('📍 Location update received:', {
        imei: data.imei,
        hasLocation: !!data.location,
        coordinates: data.location ? `${data.location.latitude}, ${data.location.longitude}` : 'N/A',
        speed: data.location?.speed || 0
      });
      
      if (data.imei === subscribedImeiRef.current) {
        if (data.location && data.location.latitude && data.location.longitude) {
          // Validate coordinates
          const lat = data.location.latitude;
          const lng = data.location.longitude;
          
          if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
            setCurrentLocation((prevLocation) => {
              setPreviousLocation(prevLocation);
              
              // Add to history with timestamp validation
              setLocationHistory(prev => {
                const newHistory = [...prev, data.location!];
                return newHistory.slice(-100); // Keep last 100 locations
              });
              
              // Enhanced movement calculation
              const moving = calculateMovement(data.location!, prevLocation);
              setIsMoving(moving);
              
              // Auto-clear movement status
              if (movementTimeoutRef.current) {
                clearTimeout(movementTimeoutRef.current);
              }
              
              if (!moving) {
                movementTimeoutRef.current = setTimeout(() => {
                  setIsMoving(false);
                }, 30000);
              }
              
              console.log(`🚗 Device ${data.imei} status: ${moving ? 'MOVING' : 'STATIC'} - Speed: ${data.location!.speed || 0} km/h`);
              
              return data.location!;
            });
          } else {
            console.error('Invalid coordinates received:', { lat, lng });
            setError('Invalid location coordinates received');
          }
        } else {
          console.log('⚠️ No valid location data for device:', data.imei);
          if (data.message) {
            setError(`Device ${data.imei}: ${data.message}`);
          } else {
            setError(`No location data available for device ${data.imei}`);
          }
        }
      }
    });

    // Enhanced subscription events
    socketInstance.on('device_subscription_success', (data: { imei: string; message?: string }) => {
      console.log('✅ Successfully subscribed to device:', data.imei);
      setIsSubscribed(true);
      setError(null);
      
      // Request initial location data
      socketInstance.emit('get_device_location', { imei: data.imei });
    });

    socketInstance.on('device_subscription_error', (data: { imei: string; error: string }) => {
      console.error('❌ Subscription failed:', data);
      setError(`Failed to subscribe to device ${data.imei}: ${data.error}`);
      setIsSubscribed(false);
    });

    socketInstance.on('device_unsubscription_success', (data: { imei: string }) => {
      console.log('✅ Successfully unsubscribed from device:', data.imei);
      setIsSubscribed(false);
      setCurrentLocation(null);
      setPreviousLocation(null);
      setLocationHistory([]);
      setIsMoving(false);
    });

    // Enhanced real-time telemetry handling
    socketInstance.on('real_time_telemetry', (data: { imei: string; data: any }) => {
      console.log('📊 Real-time telemetry received:', {
        imei: data.imei,
        hasData: !!data.data,
        coordinates: data.data?.latitude && data.data?.longitude ? 
          `${data.data.latitude}, ${data.data.longitude}` : 'N/A'
      });
      
      if (data.imei === subscribedImeiRef.current && data.data?.latitude && data.data?.longitude) {
        // Validate telemetry coordinates
        const lat = parseFloat(data.data.latitude);
        const lng = parseFloat(data.data.longitude);
        
        if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
          console.error('Invalid telemetry coordinates:', { lat, lng });
          return;
        }

        const newLocation: LocationData = {
          latitude: lat,
          longitude: lng,
          timestamp: data.data.timestamp || new Date().toISOString(),
          speed: data.data.speed ? parseFloat(data.data.speed) : 0,
          heading: data.data.heading ? parseFloat(data.data.heading) : 0,
          ignition: Boolean(data.data.ignition),
          address: data.data.address
        };
        
        setCurrentLocation((prevLocation) => {
          setPreviousLocation(prevLocation);
          
          // Add to history with deduplication
          setLocationHistory(prev => {
            const lastLocation = prev[prev.length - 1];
            if (lastLocation && 
                lastLocation.latitude === newLocation.latitude && 
                lastLocation.longitude === newLocation.longitude &&
                new Date(newLocation.timestamp).getTime() - new Date(lastLocation.timestamp).getTime() < 2000) {
              // Skip if same location within 2 seconds
              return prev;
            }
            
            const newHistory = [...prev, newLocation];
            return newHistory.slice(-100);
          });
          
          // Determine movement
          const moving = calculateMovement(newLocation, prevLocation);
          setIsMoving(moving);
          
          return newLocation;
        });
      }
    });

    // Connection stats updates
    socketInstance.on('connection_stats', (stats: ConnectionStats) => {
      setConnectionStats(stats);
    });

    // Enhanced error handling
    socketInstance.on('error', (errorData: any) => {
      console.error('🔴 Socket error:', errorData);
      setError(errorData.message || 'Real-time connection error');
      setIsSubscribed(false);
    });

    // Device status updates
    socketInstance.on('device_status_change', (data: { imei: string; status: string }) => {
      console.log('📱 Device status changed:', data);
      if (data.imei === subscribedImeiRef.current && data.status === 'offline') {
        setError(`Device ${data.imei} went offline`);
        setIsMoving(false);
      }
    });

    setSocket(socketInstance);

    return () => {
      console.log('🧹 Cleaning up socket connection...');
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (movementTimeoutRef.current) {
        clearTimeout(movementTimeoutRef.current);
      }
      if (socketInstance) {
        socketInstance.disconnect();
      }
    };
  }, [calculateMovement, reconnectAttempt]);

  // Auto-subscribe when selectedDeviceImei changes
  useEffect(() => {
    if (selectedDeviceImei && isConnected && selectedDeviceImei !== subscribedImei) {
      console.log('🔄 Device selection changed, subscribing to:', selectedDeviceImei);
      subscribeToDevice(selectedDeviceImei);
    } else if (!selectedDeviceImei && subscribedImeiRef.current) {
      console.log('🔄 No device selected, unsubscribing');
      unsubscribeFromDevice();
    }
  }, [selectedDeviceImei, isConnected, subscribedImei, subscribeToDevice, unsubscribeFromDevice]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (subscribedImeiRef.current) {
        unsubscribeFromDevice();
      }
    };
  }, [unsubscribeFromDevice]);

  return {
    currentLocation,
    locationHistory,
    isConnected,
    isSubscribed,
    error,
    isMoving,
    connectionStats,
    subscribeToDevice,
    unsubscribeFromDevice,
    reconnect,
    clearError,
  };
};
